import {
  collection,
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
  setDoc,
  type DocumentSnapshot,
} from "firebase/firestore";

import { db } from "integrations/firebase/firestore";

import { notificationPlan } from "./after-event";

/**
 * The "summary is ready" notification an organiser books when switching the
 * "After the event" section on (TNG-96).
 *
 * It is an ordinary announcement: SCHEDULE_EVENT_ANNOUNCEMENT for the instant
 * the event ends, or SEND_EVENT_ANNOUNCEMENT when that has passed. So it
 * shows in the Announcements tab like any other, where it can be edited,
 * moved or cancelled, and placeholders and feedback forms work as usual.
 *
 * Duplicates. The event stores the id of the command that booked it as
 * `afterEventNotificationCommandId`. A scheduled announcement takes its
 * command's id, so the id also names the booking. Switching on with a
 * notification (switchOnWithNotification) runs in one transaction: it reads
 * the event afresh, reads back whatever booking that id names, and only when
 * nothing will still go out does it create the command and write the id and
 * the switch. Both writes commit together or not at all, and a second
 * organiser who booked in the meantime makes the transaction refuse with
 * AlreadyBookedError. Storing the id was chosen over searching the
 * announcements by title, because the organiser may well have edited the
 * title in the Announcements tab.
 */

export type NotificationState =
  /** Nothing booked, or nothing that will still go out. */
  | { kind: "none" }
  /**
   * Filed, and the trigger has not applied it yet. `sendAt` is the send
   * time the command asked for, when it was a booking with one.
   */
  | { kind: "pending"; commandId: string; sendAt: Date | null }
  /** Booked and waiting for its send time. */
  | {
      kind: "scheduled";
      scheduledId: string;
      sendAt: Date | null;
      title: string;
      body: string;
    }
  /** Sent, or being sent. */
  | { kind: "sent" };

function toDate(value: unknown): Date | null {
  if (value instanceof Date) return value;
  if (
    value !== null &&
    typeof value === "object" &&
    "toDate" in value &&
    typeof (value as { toDate: unknown }).toDate === "function"
  ) {
    return (value as { toDate: () => Date }).toDate();
  }
  return null;
}

type Read = (path: string[]) => Promise<DocumentSnapshot>;

/** The state a stored id stands for, reading through `read`. */
async function stateOf(
  read: Read,
  eventId: string,
  commandId: string | undefined,
): Promise<NotificationState> {
  if (!commandId) return { kind: "none" };
  const command = await read(["events", eventId, "admin_commands", commandId]);
  if (!command.exists()) return { kind: "none" };
  const type: unknown = command.get("type");
  const status: unknown = command.get("status");
  if (status === "FAILED") return { kind: "none" };
  if (type === "SEND_EVENT_ANNOUNCEMENT") return { kind: "sent" };
  if (type !== "SCHEDULE_EVENT_ANNOUNCEMENT") return { kind: "none" };
  if (status !== "SUCCEEDED") {
    const payload: unknown = command.get("payload");
    const sendAt =
      payload !== null && typeof payload === "object" && "sendAt" in payload
        ? toDate((payload as { sendAt: unknown }).sendAt)
        : null;
    return { kind: "pending", commandId, sendAt };
  }
  const booking = await read([
    "events",
    eventId,
    "scheduled_announcements",
    commandId,
  ]);
  if (!booking.exists()) return { kind: "none" };
  const bookingStatus: unknown = booking.get("status");
  if (bookingStatus === "SCHEDULED")
    return {
      kind: "scheduled",
      scheduledId: commandId,
      sendAt: toDate(booking.get("sendAt")),
      title: String(booking.get("title") ?? ""),
      body: String(booking.get("body") ?? ""),
    };
  if (bookingStatus === "SENDING" || bookingStatus === "SENT")
    return { kind: "sent" };
  return { kind: "none" };
}

/** Whether the notification named by `commandId` will still go out, or did. */
export function readNotificationState(
  eventId: string,
  commandId: string | undefined,
): Promise<NotificationState> {
  return stateOf(
    ([first, ...rest]) => getDoc(doc(db, first, ...rest)),
    eventId,
    commandId,
  );
}

/**
 * The state of the notification the event names now, read from a fresh copy
 * of the event rather than whatever the page loaded earlier.
 */
export async function readEventNotificationState(
  eventId: string,
): Promise<NotificationState> {
  const event = await getDoc(doc(db, "events", eventId));
  const stored: unknown = event.get("afterEventNotificationCommandId");
  return readNotificationState(
    eventId,
    typeof stored === "string" ? stored : undefined,
  );
}

/**
 * Waits, up to `timeoutMs`, for a filed booking to be applied, so the caller
 * can act on the booking itself. Returns the last state read.
 */
export async function settledNotificationState(
  eventId: string,
  { timeoutMs = 8_000, intervalMs = 1_000 } = {},
): Promise<NotificationState> {
  const deadline = Date.now() + timeoutMs;
  let state = await readEventNotificationState(eventId);
  while (state.kind === "pending" && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
    state = await readEventNotificationState(eventId);
  }
  return state;
}

export interface NotificationText {
  title: string;
  body: string;
}

/** Another organiser's booking went in between the dialog's read and ours. */
export class AlreadyBookedError extends Error {
  constructor(readonly state: NotificationState) {
    super(
      "Another organiser booked the notification in the meantime, so no second one was booked.",
    );
    this.name = "AlreadyBookedError";
  }
}

/**
 * The event's end date or time zone, as read inside the booking transaction,
 * gives no send time that can be booked.
 */
export class NoSendTimeError extends Error {
  constructor(problem: string) {
    super(problem);
    this.name = "NoSendTimeError";
  }
}

/**
 * Switches the section on and files the notification, in one transaction.
 * Returns the new command id. Throws AlreadyBookedError, and writes nothing,
 * when the event's fresh copy names a notification that will still go out.
 *
 * The send time comes from the end date and time zone the transaction reads,
 * not from the copy the page loaded, so an end moved by another organiser
 * while the dialog was open books for the new end. When that end cannot be
 * booked it throws NoSendTimeError and writes nothing.
 */
export async function switchOnWithNotification(
  eventId: string,
  uid: string,
  text: NotificationText,
  now: Date,
): Promise<string> {
  const eventRef = doc(db, "events", eventId);
  const commandRef = doc(collection(db, "events", eventId, "admin_commands"));
  await runTransaction(db, async (transaction) => {
    const event = await transaction.get(eventRef);
    const stored: unknown = event.get("afterEventNotificationCommandId");
    const state = await stateOf(
      ([first, ...rest]) => transaction.get(doc(db, first, ...rest)),
      eventId,
      typeof stored === "string" ? stored : undefined,
    );
    if (state.kind !== "none") throw new AlreadyBookedError(state);
    const endDate: unknown = event.get("endDate");
    const timeZone: unknown = event.get("timezone");
    const plan =
      typeof endDate === "string" && typeof timeZone === "string" && timeZone
        ? notificationPlan(endDate, timeZone, now)
        : {
            mode: "unavailable" as const,
            problem:
              "The event has no end date or time zone, so no send time can be worked out.",
          };
    if (plan.mode === "unavailable") throw new NoSendTimeError(plan.problem);
    transaction.set(commandRef, {
      type:
        plan.mode === "schedule"
          ? "SCHEDULE_EVENT_ANNOUNCEMENT"
          : "SEND_EVENT_ANNOUNCEMENT",
      requestedBy: uid,
      requestedAt: serverTimestamp(),
      eventId,
      status: "REQUESTED",
      payload:
        plan.mode === "schedule"
          ? { title: text.title, body: text.body, sendAt: plan.sendAt }
          : { title: text.title, body: text.body },
    });
    transaction.update(eventRef, {
      configAfterEvent: true,
      afterEventNotificationCommandId: commandRef.id,
    });
  });
  return commandRef.id;
}

/** A command the trigger refused, or had not applied when the wait ran out. */
export class CommandNotAppliedError extends Error {
  constructor(
    readonly outcome: "failed" | "timeout",
    message: string,
  ) {
    super(message);
    this.name = "CommandNotAppliedError";
  }
}

export interface CommandWait {
  timeoutMs?: number;
  intervalMs?: number;
  /**
   * Stops the wait, for a caller that has unmounted. The command, once
   * filed, is not withdrawn: the trigger still applies it.
   */
  signal?: AbortSignal;
}

/** What a wait throws once its signal is aborted. */
export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

function aborted(): DOMException {
  return new DOMException("The wait was stopped.", "AbortError");
}

/** Resolves after `ms`, or rejects with an AbortError once `signal` aborts. */
function pause(ms: number, signal: AbortSignal | undefined): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(aborted());
      return;
    }
    const stop = () => {
      clearTimeout(timer);
      reject(aborted());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", stop);
      resolve();
    }, ms);
    signal?.addEventListener("abort", stop, { once: true });
  });
}

/**
 * Files an admin command and waits, up to `timeoutMs`, for the trigger to
 * settle it. Resolves once it SUCCEEDED; throws CommandNotAppliedError when
 * it FAILED (with the trigger's reason) or was still unsettled at the end,
 * and an AbortError (see isAbortError) once `signal` aborts.
 */
async function fileCommandAndWait(
  eventId: string,
  uid: string,
  type: string,
  payload: Record<string, unknown>,
  { timeoutMs = 15_000, intervalMs = 500, signal }: CommandWait = {},
): Promise<void> {
  if (signal?.aborted) throw aborted();
  const ref = doc(collection(db, "events", eventId, "admin_commands"));
  await setDoc(ref, {
    type,
    requestedBy: uid,
    requestedAt: serverTimestamp(),
    eventId,
    status: "REQUESTED",
    payload,
  });
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    if (signal?.aborted) throw aborted();
    const command = await getDoc(
      doc(db, "events", eventId, "admin_commands", ref.id),
    );
    const status: unknown = command.exists() ? command.get("status") : null;
    if (status === "SUCCEEDED") return;
    if (status === "FAILED") {
      const error: unknown = command.get("error");
      throw new CommandNotAppliedError(
        "failed",
        typeof error === "string" && error ? error : "The command failed.",
      );
    }
    if (Date.now() >= deadline)
      throw new CommandNotAppliedError(
        "timeout",
        "It has not been applied yet.",
      );
    await pause(intervalMs, signal);
  }
}

/**
 * Cancels a booked notification, as the Announcements tab's Cancel does, and
 * resolves only once the trigger has applied the cancel.
 */
export function cancelNotification(
  eventId: string,
  uid: string,
  scheduledId: string,
  wait?: CommandWait,
): Promise<void> {
  return fileCommandAndWait(
    eventId,
    uid,
    "CANCEL_SCHEDULED_ANNOUNCEMENT",
    { scheduledAnnouncementId: scheduledId },
    wait,
  );
}

/**
 * Moves a booked notification to a new send time, keeping its text and any
 * feedback form, as an edit in the Announcements tab would. Resolves only
 * once the trigger has applied the move.
 */
export function moveNotification(
  eventId: string,
  uid: string,
  booking: Extract<NotificationState, { kind: "scheduled" }>,
  sendAt: Date,
  wait?: CommandWait,
): Promise<void> {
  return fileCommandAndWait(
    eventId,
    uid,
    "UPDATE_SCHEDULED_ANNOUNCEMENT",
    {
      scheduledAnnouncementId: booking.scheduledId,
      title: booking.title,
      body: booking.body,
      sendAt,
    },
    wait,
  );
}
